import { useState, useEffect, useRef } from "react";
import { db } from "../lib/db";
import { GROQ_KEY, buildAIContext } from "../lib/ai";
import { AI_CHAT_KEY } from "../lib/utils";

const AI_ROLES = ["supervisor", "finance", "admin"];

const INIT_MSG = { role: "assistant", content: "Hi! Ask me anything about your timesheet data — hours by project, R&D breakdowns, top employees, or anything else." };

export function AIAssistant({ user }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem(AI_CHAT_KEY);
      return saved ? JSON.parse(saved) : [INIT_MSG];
    } catch { return [INIT_MSG]; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [corrections, setCorrections] = useState({});
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!AI_ROLES.includes(user?.role)) return;
    Promise.all([
      db.get("entries", "order=date.asc"),
      db.get("project_codes", "order=code.asc"),
      db.get("employees", "order=id.asc")
    ]).then(([ents, pcs, emps]) => {
      setEntries(ents || []); setProjectCodes(pcs || []); setEmployees(emps || []); setDataLoaded(true);
    });
  }, [user?.role]);

  // Persist chat to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(AI_CHAT_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        inputRef.current?.focus();
      }, 150);
    }
  }, [open]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!AI_ROLES.includes(user?.role)) return null;

  const customNotes = localStorage.getItem("es_ai_notes") || "";

  const callAI = async (msgs) => {
    const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${GROQ_KEY}` },
      body: JSON.stringify({
        model: "llama-3.3-70b-versatile",
        max_tokens: 800,
        messages: [
          { role: "system", content: buildAIContext(entries, projectCodes, customNotes, employees) },
          ...msgs.map(m => ({ role: m.role, content: m.content }))
        ]
      })
    });
    const data = await res.json();
    return data.choices?.[0]?.message?.content || "Sorry, I could not get a response.";
  };

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    const newMessages = [...messages, { role: "user", content: q }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const reply = await callAI(newMessages);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection error. Please try again." }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const submitCorrection = async (msgIndex) => {
    const correctionText = corrections[msgIndex]?.trim();
    const msg = correctionText
      ? `That answer was incorrect. ${correctionText}`
      : "That answer was incorrect. Please reconsider and try again with a different approach.";
    setFeedback(f => ({ ...f, [msgIndex]: "corrected" }));
    const newMessages = [...messages, { role: "user", content: msg }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const reply = await callAI(newMessages);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection error. Please try again." }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const suggestions = ["Who worked the most hours?", "R&D vs regular hours split?", "Which project has the most hours?", "Give me a summary"];

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="Open AI Assistant"
          style={{
            position: "fixed", bottom: 20, right: 20, zIndex: 500,
            background: "#1a1f2e", color: "#fff", border: "none", borderRadius: 28,
            padding: "11px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
            fontFamily: "'Barlow Condensed',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1,
            boxShadow: "0 4px 20px rgba(0,0,0,0.22)", transition: "transform .15s, box-shadow .15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.28)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.22)"; }}
        >
          <span style={{ fontSize: 17, color: "#c8a84b", lineHeight: 1 }}>✦</span>
          <span>AI</span>
          {messages.length > 1 && <span style={{ background: "#c8a84b", color: "#1a1f2e", borderRadius: 10, fontSize: 11, fontWeight: 800, padding: "1px 6px", marginLeft: 2 }}>{Math.floor(messages.length / 2)}</span>}
        </button>
      )}

      {open && (
        <div style={{
          position: "fixed", bottom: 20, right: 20, zIndex: 500,
          width: 370, maxWidth: "calc(100vw - 24px)",
          background: "#fff", borderRadius: 12, border: "1px solid #d8dce8",
          boxShadow: "0 8px 36px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{ background: "#1a1f2e", padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 17, color: "#c8a84b", lineHeight: 1 }}>✦</span>
              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#fff" }}>AI Assistant</span>
              {!dataLoaded && <span style={{ fontSize: 11, color: "#6b7280" }}>· loading…</span>}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => { setMessages([INIT_MSG]); setFeedback({}); setCorrections({}); }} title="Clear chat" style={{ background: "transparent", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 13, padding: "0 4px" }}>↺</button>
              <button onClick={() => setOpen(false)} title="Minimise" style={{ background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: "0 4px", display: "flex", alignItems: "center" }}>−</button>
            </div>
          </div>

          <div style={{ overflowY: "auto", padding: "14px 14px 8px", display: "flex", flexDirection: "column", gap: 12, maxHeight: 400, minHeight: 160 }}>
            {messages.map((m, i) => (
              <div key={i}>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexDirection: m.role === "user" ? "row-reverse" : "row" }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, background: m.role === "user" ? "#1a1f2e" : "#c8a84b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff" }}>
                    {m.role === "user" ? "U" : "✦"}
                  </div>
                  <div style={{ maxWidth: "82%", padding: "8px 11px", borderRadius: m.role === "user" ? "12px 4px 12px 12px" : "4px 12px 12px 12px", background: m.role === "user" ? "#1a1f2e" : "#f8f9fc", color: m.role === "user" ? "#fff" : "#1a1f2e", fontSize: 13, lineHeight: 1.6, border: m.role === "user" ? "none" : "1px solid #e4e7f0", whiteSpace: "pre-wrap" }}>
                    {m.content}
                  </div>
                </div>

                {m.role === "assistant" && i > 0 && (
                  <div style={{ paddingLeft: 34, marginTop: 4 }}>
                    {!feedback[i] && (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <span style={{ fontSize: 11, color: "#9ca3af", marginRight: 2 }}>Helpful?</span>
                        <button onClick={() => setFeedback(f => ({ ...f, [i]: "up" }))} title="Correct" style={{ background: "none", border: "1px solid #e4e7f0", borderRadius: 4, padding: "2px 7px", cursor: "pointer", fontSize: 13, lineHeight: 1.4 }}>👍</button>
                        <button onClick={() => setFeedback(f => ({ ...f, [i]: "correcting" }))} title="Wrong" style={{ background: "none", border: "1px solid #e4e7f0", borderRadius: 4, padding: "2px 7px", cursor: "pointer", fontSize: 13, lineHeight: 1.4 }}>👎</button>
                      </div>
                    )}
                    {feedback[i] === "up" && <div style={{ fontSize: 11, color: "#2a8a2a" }}>✓ Marked as helpful</div>}
                    {feedback[i] === "corrected" && <div style={{ fontSize: 11, color: "#c8a84b" }}>↩ Correction sent — see updated answer below</div>}
                    {feedback[i] === "correcting" && (
                      <div style={{ marginTop: 4 }}>
                        <div style={{ fontSize: 11, color: "#cc4444", marginBottom: 4 }}>What was wrong? (optional)</div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <input autoFocus className="form-input" placeholder="e.g. the hours total was off…" value={corrections[i] || ""} onChange={e => setCorrections(c => ({ ...c, [i]: e.target.value }))} onKeyDown={e => e.key === "Enter" && submitCorrection(i)} style={{ fontSize: 12, padding: "6px 8px", flex: 1 }} />
                          <button onClick={() => submitCorrection(i)} style={{ background: "#cc4444", color: "#fff", border: "none", borderRadius: 5, padding: "6px 10px", cursor: "pointer", fontFamily: "'Barlow Condensed',sans-serif", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>Send</button>
                          <button onClick={() => setFeedback(f => ({ ...f, [i]: null }))} style={{ background: "none", border: "1px solid #e4e7f0", borderRadius: 5, padding: "6px 8px", cursor: "pointer", fontSize: 12, color: "#6b7280" }}>✕</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#c8a84b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff" }}>✦</div>
                <div style={{ padding: "10px 14px", borderRadius: "4px 12px 12px 12px", background: "#f8f9fc", border: "1px solid #e4e7f0", display: "flex", gap: 5, alignItems: "center" }}>
                  <style>{`@keyframes aibounce{0%,80%,100%{transform:scale(0)}40%{transform:scale(1)}}`}</style>
                  {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#c8a84b", animation: `aibounce 1s ease-in-out ${i*0.2}s infinite` }} />)}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div style={{ padding: "0 14px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {suggestions.map(s => (
                <button key={s} onClick={() => setInput(s)} style={{ background: "#f0f2f5", border: "1px solid #e4e7f0", color: "#374151", padding: "5px 10px", borderRadius: 20, fontSize: 12, cursor: "pointer" }}>{s}</button>
              ))}
            </div>
          )}

          <div style={{ borderTop: "1px solid #e4e7f0", padding: "10px 12px", display: "flex", gap: 8, alignItems: "center", background: "#fff", flexShrink: 0 }}>
            <input ref={inputRef} className="form-input" placeholder={dataLoaded ? "Ask anything…" : "Loading data…"} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} disabled={loading || !dataLoaded} style={{ fontSize: 13, flex: 1, padding: "9px 10px" }} />
            <button onClick={send} disabled={loading || !input.trim() || !dataLoaded} style={{ background: "#1a1f2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 14px", cursor: "pointer", fontFamily: "'Barlow Condensed',sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", opacity: (loading || !input.trim() || !dataLoaded) ? 0.45 : 1, whiteSpace: "nowrap" }}>↑</button>
          </div>
        </div>
      )}
    </>
  );
}
